/**
 * Merchant detail drawer: full info via getMerchantsRequestFullInfo(email).
 * Blocks: Данные (ID, email, status, date, partner), Компания (в т.ч. юр. адрес), Банк, Договор (контракт) + кнопка «Сформировать отчёт».
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as api from '../../modules/api';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import DetailDrawer from '../components/DetailDrawer';
import { Section, Field, CopyableField } from '../components/DetailDrawerContent';
import { loadAdminRuntime } from '../utils/adminRuntime';
import { merchantFieldsFor, showMerchantField } from '../utils/merchantFields';

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = typeof value === 'number' ? dayjs(value) : dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

function formatDateOnly(value) {
  if (value == null || value === '') return '—';
  const d = typeof value === 'number' ? dayjs(value) : dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
}

function formatBool(value, t) {
  if (value === true) return t('common.yes');
  if (value === false) return t('common.no');
  return '—';
}

/** Дата создания мерчанта: поддерживаем разные ключи и форматы (timestamp, ISO). */
function getCreateDate(displayInfo) {
  if (!displayInfo) return null;
  const raw = displayInfo.created ?? displayInfo.createdDate ?? displayInfo.createDate ?? displayInfo.registrationDate;
  if (raw == null || raw === '') return null;
  const d = typeof raw === 'number' ? dayjs(raw) : dayjs(raw);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : null;
}

function formatDirectorFio(displayInfo, includeMiddleName = true) {
  if (!displayInfo) return '—';
  const parts = includeMiddleName
    ? [displayInfo.surname, displayInfo.name, displayInfo.middleName]
    : [displayInfo.surname, displayInfo.name];
  return parts.filter(Boolean).length ? parts.filter(Boolean).join(' ') : '—';
}

export default function MerchantDetailDrawer({ merchant, open, onClose, onRefresh }) {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [fullInfo, setFullInfo] = useState(null);
  const [fullInfoLoading, setFullInfoLoading] = useState(false);
  const [fullInfoError, setFullInfoError] = useState(null);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportStartDate, setReportStartDate] = useState(null);
  const [reportEndDate, setReportEndDate] = useState(null);
  const [reportSending, setReportSending] = useState(false);
  const [runtime, setRuntime] = useState(null);

  const email = merchant?.email ?? merchant?.login ?? '';
  const locked = Boolean(merchant?.lock ?? merchant?.locked ?? fullInfo?.lock ?? fullInfo?.locked ?? false);
  const isIndividual = fullInfo?.typeIndividualMerchant != null;
  const doc = fullInfo?.document;

  useEffect(() => {
    loadAdminRuntime().then((data) => setRuntime(data));
  }, []);

  useEffect(() => {
    if (!open || !email) {
      setFullInfo(null);
      setFullInfoError(null);
      return;
    }
    setFullInfoLoading(true);
    setFullInfoError(null);
    api
      .getMerchantsRequestFullInfo(email)
      .then((res) => {
        const data = res?.data;
        setFullInfo(data && typeof data === 'object' ? data : null);
      })
      .catch((e) => {
        setFullInfoError(e?.message || t('drawer.merchant.failedToLoad'));
        setFullInfo(null);
      })
      .finally(() => setFullInfoLoading(false));
  }, [open, email, t]);

  const handleCopy = useCallback(
    (text) => {
      if (!text || text === '—') return;
      navigator.clipboard.writeText(String(text)).then(
        () => notifications.show(t('drawer.copied'), { severity: 'success', autoHideDuration: 2000 }),
        () => notifications.show(t('drawer.copyFailed'), { severity: 'error' })
      );
    },
    [notifications, t]
  );

  const handleLockClick = useCallback(() => setLockDialogOpen(true), []);
  const handleLockCancel = useCallback(() => setLockDialogOpen(false), []);

  const handleLockConfirm = useCallback(() => {
    if (!email) return;
    const newLocked = !locked;
    setLockLoading(true);
    api
      .lockMerchant(email, newLocked)
      .then(() => {
        setLockDialogOpen(false);
        if (typeof onRefresh === 'function') onRefresh();
        setFullInfo((prev) => (prev ? { ...prev, lock: newLocked, locked: newLocked } : null));
        notifications.show(
          newLocked ? t('drawer.merchant.merchantLocked') : t('drawer.merchant.merchantUnlocked'),
          { severity: 'success', autoHideDuration: 3000 }
        );
      })
      .catch((e) => {
        notifications.show(e?.message ?? t('drawer.actionFailed'), { severity: 'error' });
      })
      .finally(() => setLockLoading(false));
  }, [email, locked, onRefresh, notifications, t]);

  const handleReportClick = useCallback(() => {
    setReportStartDate(null);
    setReportEndDate(null);
    setReportDialogOpen(true);
  }, []);

  const handleReportClose = useCallback(() => setReportDialogOpen(false), []);

  const handleReportSubmit = useCallback(() => {
    const start = reportStartDate ? dayjs(reportStartDate).startOf('day') : null;
    const end = reportEndDate ? dayjs(reportEndDate).startOf('day') : null;
    if (!start?.isValid()) {
      notifications.show(t('drawer.merchant.reportSelectPeriod'), { severity: 'warning' });
      return;
    }
    const StartDate = start.format('YYYY-MM-DDTHH:mm:ss.SSS');
    const EndDate = (end?.isValid() ? end : dayjs()).format('YYYY-MM-DDTHH:mm:ss.SSS');
    setReportSending(true);
    api
      .SentRepo({ email, StartDate, EndDate })
      .then(() => {
        setReportDialogOpen(false);
        notifications.show(t('drawer.merchant.reportSent'), { severity: 'success', autoHideDuration: 3000 });
      })
      .catch((e) => {
        notifications.show(e?.message ?? t('drawer.actionFailed'), { severity: 'error' });
      })
      .finally(() => setReportSending(false));
  }, [email, reportStartDate, reportEndDate, notifications, t]);

  const displayInfo = fullInfo ?? merchant;
  const fields = merchantFieldsFor(runtime, displayInfo);
  const show = (field) => showMerchantField(fields, field);
  const showCompany =
    displayInfo &&
    (show('legalName') ||
      show('address') ||
      show('inn') ||
      show('edrpou') ||
      show('npwp') ||
      show('givenName') ||
      show('familyName') ||
      show('phone'));
  const showBank = displayInfo && (show('bankName') || show('accountNumber') || show('bic') || show('iban'));

  return (
    <>
      <DetailDrawer title={t('drawer.merchant.title')} open={open} onClose={onClose}>
        <Box sx={{ minWidth: 0 }}>
          {!merchant ? (
            <Typography color="text.secondary">{t('drawer.merchant.noSelection')}</Typography>
          ) : fullInfoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : fullInfoError ? (
            <Typography color="error" variant="body2">{fullInfoError}</Typography>
          ) : (
            <>
              {/* 1. Данные: только ID, email, статус, дата создания, партнёр */}
              <Section title={t('drawer.merchant.details')}>
                {displayInfo?.id != null && (
                  <Field label={t('drawer.merchant.id')} value={displayInfo.id} />
                )}
                <CopyableField label={t('drawer.merchant.email')} value={email || displayInfo?.email} onCopy={handleCopy} copyTooltip={t('drawer.copy')} />
                <Field
                  label={t('drawer.merchant.status')}
                  value={
                    <Chip
                      size="small"
                      label={locked ? t('drawer.merchant.blocked') : t('drawer.merchant.active')}
                      color={locked ? 'error' : 'success'}
                      variant="outlined"
                    />
                  }
                />
                {displayInfo && (
                  <Field label={t('drawer.merchant.createDate')} value={getCreateDate(displayInfo) ?? formatDate(displayInfo.created ?? displayInfo.createdDate ?? displayInfo.createDate) ?? '—'} />
                )}
                <Field label={t('drawer.merchant.partner')} value={displayInfo?.partnerEmail} />
              </Section>

              {/* 2. Компания: состав полей из runtime merchantFields (не ИНН/БИК для international) */}
              {showCompany && (
                <Section title={t('drawer.merchant.company')}>
                  {show('legalName') && (
                    <Field label={t('drawer.merchant.nameOfLegalEntity')} value={displayInfo.nameOfLegalEntity} />
                  )}
                  {show('address') && (
                    <Field label={t('drawer.merchant.legalAddress')} value={displayInfo.legalAddress} />
                  )}
                  {show('inn') && (
                    <Field label={t('drawer.merchant.inn')} value={displayInfo.inn} />
                  )}
                  {show('edrpou') && (
                    <Field label={t('drawer.merchant.edrpou')} value={displayInfo.edrpou} />
                  )}
                  {show('npwp') && (
                    <Field label={t('drawer.merchant.npwp')} value={displayInfo.npwp} />
                  )}
                  {(show('givenName') || show('familyName')) && (
                    <Field
                      label={t('drawer.merchant.directorFio')}
                      value={formatDirectorFio(displayInfo, show('middleName'))}
                    />
                  )}
                  <Field label={t('drawer.merchant.position')} value={displayInfo.position} />
                  {show('phone') && (
                    <CopyableField label={t('drawer.merchant.phoneNumber')} value={displayInfo.phoneNumber} onCopy={handleCopy} copyTooltip={t('drawer.copy')} />
                  )}
                </Section>
              )}

              {/* 3. Банк: БИК только для RU; UA — IBAN; ID в манифесте банка нет */}
              {showBank && (
                <Section title={t('drawer.merchant.bank')}>
                  {show('bankName') && (
                    <Field label={t('drawer.merchant.bankName')} value={displayInfo.bankName} />
                  )}
                  {show('accountNumber') && (
                    <Field label={t('drawer.merchant.accountNumber')} value={displayInfo.accountNumber} />
                  )}
                  {show('bic') && (
                    <Field label={t('drawer.merchant.bic')} value={displayInfo.bic} />
                  )}
                  {show('iban') && (
                    <Field label={t('drawer.merchant.iban')} value={displayInfo.iban} />
                  )}
                </Section>
              )}

              {/* 4. Договор: НДС только на Chargex (RU) */}
              {displayInfo && (
                <Section title={t('drawer.merchant.contract')}>
                  <Field label={t('drawer.merchant.contractNumber')} value={displayInfo.contractId} />
                  <Field label={t('drawer.merchant.contractDate')} value={formatDateOnly(displayInfo.contractDate) || displayInfo.contractDate} />
                  <Field label={t('drawer.merchant.concessionAmount')} value={displayInfo.revShare} />
                  {show('withNDS') && (
                    <Field label={t('drawer.merchant.withNDS')} value={formatBool(displayInfo.withNDS, t)} />
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SummarizeOutlinedIcon />}
                    onClick={handleReportClick}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {t('drawer.merchant.generateReport')}
                  </Button>
                </Section>
              )}

              {/* Физ. лицо: тип */}
              {isIndividual && (
                <Section title={t('drawer.merchant.individual')}>
                  <Field label={t('drawer.merchant.typeIndividualMerchant')} value={fullInfo.typeIndividualMerchant} />
                </Section>
              )}

              {/* Документ (физ. лицо) */}
              {doc && typeof doc === 'object' && (
                <Section title={t('drawer.merchant.document')}>
                  <Field label={t('drawer.merchant.actualAddress')} value={doc.actualAddress} />
                  <Field label={t('drawer.merchant.addressOfRegistration')} value={doc.addressOfRegistration} />
                  <Field label={t('drawer.merchant.dateOfIssue')} value={doc.dateOfIssue} />
                  <Field label={t('drawer.merchant.documentNumber')} value={doc.documentNumber} />
                  <Field label={t('drawer.merchant.issueBy')} value={doc.issueBy} />
                  <Field label={t('drawer.merchant.issueCode')} value={doc.issueCode} />
                  <Field label={t('drawer.merchant.series')} value={doc.series} />
                </Section>
              )}

              <Button
                variant="outlined"
                color={locked ? 'success' : 'error'}
                startIcon={locked ? <LockOpenIcon /> : <LockIcon />}
                onClick={handleLockClick}
                fullWidth
                sx={{ mt: 1 }}
              >
                {locked ? t('drawer.merchant.unlockMerchant') : t('drawer.merchant.blockMerchant')}
              </Button>
            </>
          )}
        </Box>
      </DetailDrawer>

      {/* Lock dialog */}
      <Dialog open={lockDialogOpen} onClose={handleLockCancel}>
        <DialogTitle>
          {locked ? t('drawer.merchant.unlockConfirmTitle') : t('drawer.merchant.blockConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {locked
              ? t('drawer.merchant.unlockConfirmMessage', { email })
              : t('drawer.merchant.blockConfirmMessage', { email })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLockCancel}>{t('drawer.cancel')}</Button>
          <Button onClick={handleLockConfirm} color={locked ? 'success' : 'error'} variant="contained" disabled={lockLoading}>
            {lockLoading ? t('drawer.saving') : locked ? t('drawer.merchant.unlockMerchant') : t('drawer.merchant.blockMerchant')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report period dialog */}
      <Dialog open={reportDialogOpen} onClose={handleReportClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('drawer.merchant.generateReport')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('drawer.merchant.reportPeriodHint')}</DialogContentText>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DatePicker
                label={t('drawer.merchant.reportStartDate')}
                value={reportStartDate}
                onChange={(d) => setReportStartDate(d)}
                format="DD.MM.YYYY"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DatePicker
                label={t('drawer.merchant.reportEndDate')}
                value={reportEndDate}
                onChange={(d) => setReportEndDate(d)}
                minDate={reportStartDate ? dayjs(reportStartDate) : undefined}
                format="DD.MM.YYYY"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReportClose}>{t('drawer.cancel')}</Button>
          <Button onClick={handleReportSubmit} variant="contained" disabled={reportSending}>
            {reportSending ? t('drawer.saving') : t('drawer.merchant.sendReport')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
